import { ApiError, NetworkError, UnauthorizedError } from './errors'
import type { ApiErrorBody, ApiEnvelope, ApiListEnvelope } from './types'

/**
 * Držiteľ relácie. Klient nevie, kde token býva — dostane rozhranie.
 *
 * Vďaka tomu sa dá klient testovať bez Pinie a bez prehliadača a produkčná
 * implementácia môže access token držať iba v pamäti (refresh token je
 * httpOnly cookie, ktorú JavaScript nevidí).
 */
export interface ApiSession {
	getAccessToken(): string | null
	setAccessToken(token: string | null): void
	/**
	 * Vymení refresh token za nový access token.
	 * Vráti `null`, keď obnovenie neprešlo — vtedy je relácia stratená.
	 */
	refresh(): Promise<string | null>
	/** Zavolá sa raz, keď sa relácia definitívne stratila (redirect na prihlásenie). */
	onAuthLost(): void
}

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
	method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
	/** Serializuje sa ako JSON; `FormData` sa posiela tak, ako je (import cenníka). */
	body?: unknown
	query?: Readonly<Record<string, QueryValue | readonly QueryValue[]>>
	headers?: Readonly<Record<string, string>>
	signal?: AbortSignal
	/**
	 * Vypne obnovenie tokenu pri 401.
	 *
	 * Prihlásenie musí 401 vrátiť volajúcemu ako „zlé heslo“, nie sa ho pokúšať
	 * obnoviť — inak by používateľ videl „relácia vypršala“ pri preklepe.
	 */
	skipAuthRefresh?: boolean
}

export interface ApiClientOptions {
	/** Predvolene relatívne `/api/v1` — rovnaký pôvod, aby refresh cookie prešla. */
	baseUrl?: string
	session: ApiSession
	fetchImpl?: typeof fetch
}

const JSON_CONTENT_TYPE = 'application/json'

/**
 * Typovaný klient nad `fetch`.
 *
 * Rieši tri veci, ktoré by sa inak opakovali v každom volaní: JWT hlavičku,
 * jednorazové obnovenie tokenu pri 401 a rozbalenie obálky `{"data": ...}`
 * vrátane prekladu chybového tela na `ApiError`.
 */
export class ApiClient {
	private readonly baseUrl: string
	private readonly session: ApiSession
	private readonly fetchImpl: typeof fetch

	/**
	 * Prebiehajúce obnovenie tokenu.
	 *
	 * Pri načítaní dashboardu beží niekoľko dopytov naraz a všetky dostanú 401
	 * súčasne. Bez tohto poľa by každý spustil vlastný refresh a rotujúci
	 * refresh token by sa navzájom zneplatnili — používateľa by to odhlásilo.
	 */
	private refreshInFlight: Promise<string | null> | null = null

	constructor(options: ApiClientOptions) {
		this.baseUrl = (options.baseUrl ?? '/api/v1').replace(/\/+$/, '')
		this.session = options.session
		this.fetchImpl = options.fetchImpl ?? ((input, init) => globalThis.fetch(input, init))
	}

	get<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
		return this.request<T>(path, { ...options, method: 'GET' })
	}

	post<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
		return this.request<T>(path, { ...options, method: 'POST', body })
	}

	patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
		return this.request<T>(path, { ...options, method: 'PATCH', body })
	}

	put<T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
		return this.request<T>(path, { ...options, method: 'PUT', body })
	}

	delete<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> {
		return this.request<T>(path, { ...options, method: 'DELETE' })
	}

	/** Zoznam vracia aj `meta` — tabuľka potrebuje `total` na stránkovanie. */
	async list<T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<ApiListEnvelope<T>> {
		const envelope = await this.send<ApiListEnvelope<T>>(path, { ...options, method: 'GET' }, false)

		return envelope
	}

	/** Vráti obsah `data`; obálku rieši klient, nie volajúci. */
	async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
		return this.send<T>(path, options, true)
	}

	private async send<T>(path: string, options: RequestOptions, unwrap: boolean): Promise<T> {
		const response = await this.fetchWithAuth(path, options, false)

		return this.readBody<T>(response, unwrap)
	}

	/**
	 * Jedno volanie vrátane obnovenia tokenu.
	 *
	 * `retried` je poistka proti slučke: po neúspešnom druhom pokuse sa už
	 * neobnovuje, aj keby server vrátil 401 znova.
	 */
	private async fetchWithAuth(path: string, options: RequestOptions, retried: boolean): Promise<Response> {
		const response = await this.rawFetch(path, options)

		if (response.status !== 401 || retried || options.skipAuthRefresh === true) {
			return response
		}

		const token = await this.refreshOnce()
		if (token === null) {
			this.session.onAuthLost()

			throw new UnauthorizedError()
		}

		return this.fetchWithAuth(path, options, true)
	}

	/** Zlúči súbežné obnovenia do jedného volania. */
	private async refreshOnce(): Promise<string | null> {
		this.refreshInFlight ??= this.session
			.refresh()
			.then((token) => {
				this.session.setAccessToken(token)

				return token
			})
			.catch(() => {
				this.session.setAccessToken(null)

				return null
			})
			.finally(() => {
				this.refreshInFlight = null
			})

		return this.refreshInFlight
	}

	private async rawFetch(path: string, options: RequestOptions): Promise<Response> {
		const headers = new Headers({ Accept: JSON_CONTENT_TYPE, ...options.headers })
		const token = this.session.getAccessToken()
		if (token !== null && token !== '') {
			headers.set('Authorization', `Bearer ${token}`)
		}

		const init: RequestInit = {
			method: options.method ?? 'GET',
			headers,
			// Refresh token je httpOnly cookie — bez `include` by sa neposlala.
			credentials: 'include',
		}
		if (options.signal !== undefined) {
			init.signal = options.signal
		}

		if (options.body !== undefined) {
			if (options.body instanceof FormData) {
				init.body = options.body
			} else {
				headers.set('Content-Type', JSON_CONTENT_TYPE)
				init.body = JSON.stringify(options.body)
			}
		}

		try {
			return await this.fetchImpl(this.buildUrl(path, options.query), init)
		} catch (cause) {
			throw new NetworkError(cause)
		}
	}

	private buildUrl(path: string, query: RequestOptions['query']): string {
		const url = `${this.baseUrl}/${path.replace(/^\/+/, '')}`
		if (query === undefined) {
			return url
		}

		const params = new URLSearchParams()
		for (const [key, value] of Object.entries(query)) {
			for (const item of Array.isArray(value) ? value : [value]) {
				if (item !== undefined && item !== null && item !== '') {
					params.append(key, String(item))
				}
			}
		}

		const serialized = params.toString()

		return serialized === '' ? url : `${url}?${serialized}`
	}

	private async readBody<T>(response: Response, unwrap: boolean): Promise<T> {
		const payload = await this.parseJson(response)

		if (!response.ok) {
			throw toApiError(response.status, payload)
		}

		if (payload === undefined) {
			// 204 No Content — napr. zmazanie konceptu. Volajúci typuje `void`.
			return undefined as T
		}

		if (!unwrap) {
			return payload as T
		}

		if (isEnvelope(payload)) {
			return payload.data as T
		}

		throw new ApiError(
			response.status,
			'MALFORMED_RESPONSE',
			'Odpoveď servera má neočakávaný tvar. Kontaktujte podporu.',
		)
	}

	private async parseJson(response: Response): Promise<unknown> {
		if (response.status === 204 || response.status === 205) {
			return undefined
		}

		const text = await response.text()
		if (text === '') {
			return undefined
		}

		try {
			return JSON.parse(text) as unknown
		} catch {
			throw new ApiError(
				response.status,
				'MALFORMED_RESPONSE',
				'Odpoveď servera sa nepodarilo spracovať. Skúste to prosím znova.',
			)
		}
	}
}

function isEnvelope(payload: unknown): payload is ApiEnvelope<unknown> {
	return typeof payload === 'object' && payload !== null && 'data' in payload
}

function isErrorBody(payload: unknown): payload is ApiErrorBody {
	if (typeof payload !== 'object' || payload === null || !('error' in payload)) {
		return false
	}

	const error = (payload as { error: unknown }).error

	return typeof error === 'object' && error !== null && 'code' in error && 'message' in error
}

/** Preloží chybové telo na výnimku; 401 má vlastnú triedu kvôli vetveniu v UI. */
function toApiError(status: number, payload: unknown): ApiError {
	if (isErrorBody(payload)) {
		const { code, message, details } = payload.error

		return status === 401 ? new UnauthorizedError(message) : new ApiError(status, code, message, details ?? [])
	}

	if (status === 401) {
		return new UnauthorizedError()
	}

	return new ApiError(status, 'HTTP_ERROR', 'Požiadavka zlyhala. Skúste to prosím znova.')
}

export function createApiClient(options: ApiClientOptions): ApiClient {
	return new ApiClient(options)
}
